"use client";

import React, { createContext, useContext } from "react";
import type {
  BaseEntity,
  SimpleTableConfig,
  EntityHooks,
  FieldValue,
  SimpleFieldConfig,
  SerializedViewState,
} from "../types";
import type { AuthenticatedUser } from "@/lib/types/unified";
import type { QuickFilterState } from "@/components/shared/DataTable-modular/types";

/**
 * Consolidated context for SimpleTable
 * Eliminates prop drilling by providing all table state and handlers via context
 */

export interface SimpleTableContextValue<TData extends BaseEntity> {
  // Configuration
  config: SimpleTableConfig<TData>;
  visibleFields: Array<[string, SimpleFieldConfig]>;

  // Data
  data: TData[];
  processedData: TData[];
  paginatedData: TData[];
  totalPages: number;

  // State - All table state values
  state: {
    selectedItems: Set<string>;
    setSelectedItems: (items: Set<string> | ((prev: Set<string>) => Set<string>)) => void;
    editingCell: { rowId: string; fieldId: string } | null;
    setEditingCell: (cell: { rowId: string; fieldId: string } | null) => void;
    editValue: string;
    setEditValue: (value: string) => void;
    columnWidths: Record<string, number>;
    setColumnWidths: (
      widths: Record<string, number> | ((prev: Record<string, number>) => Record<string, number>)
    ) => void;
    sortColumns: Array<{ field: string; direction: "asc" | "desc"; priority: number }>;
    setSortColumns: (
      columns: Array<{ field: string; direction: "asc" | "desc"; priority: number }>
    ) => void;
    sortField: string;
    setSortField: (field: string) => void;
    sortDirection: "asc" | "desc";
    setSortDirection: (direction: "asc" | "desc") => void;
    draggedColumn: string | null;
    setDraggedColumn: (column: string | null) => void;
    showFloatingBulkActions: boolean;
    setShowFloatingBulkActions: (show: boolean) => void;
    columnOrder: string[];
    setColumnOrder: (order: string[]) => void;
    effectiveSearchTerm: string;
    effectiveSetSearchTerm: (term: string) => void;
    effectiveFilters: Record<string, string | number | boolean | string[] | null | undefined>;
    effectiveSetFilters: (
      filters: Record<string, string | number | boolean | string[] | null | undefined>
    ) => void;
    effectiveAdvancedFilters: Record<string, unknown>;
    effectiveSetAdvancedFilters: (filters: Record<string, unknown>) => void;
    effectiveFilterOperator: "AND" | "OR";
    effectiveSetFilterOperator: (operator: "AND" | "OR") => void;
    showAdvancedFilters: boolean;
    setShowAdvancedFilters: (show: boolean) => void;
    quickFilterState: QuickFilterState;
    setQuickFilterState: (
      state: QuickFilterState | ((prev: QuickFilterState) => QuickFilterState)
    ) => void;
    customRangeModal: { isOpen: boolean; fieldId: string; fieldLabel: string };
    setCustomRangeModal: (modal: { isOpen: boolean; fieldId: string; fieldLabel: string }) => void;
    effectiveCurrentPage: number;
    effectiveSetCurrentPage: (page: number) => void;
    effectivePageSize: number;
    effectiveSetPageSize: (size: number) => void;
  };

  // Callbacks - All event handlers
  callbacks: {
    handleSelectItem: (itemId: string, selected: boolean) => void;
    handleSelectAll: (selected: boolean) => void;
    handleCellEditCallback: (rowId: string, fieldId: string, value: FieldValue) => void;
    handleCellSaveCallback: (rowId: string, fieldId: string) => Promise<void>;
    handleCellCancelCallback: () => void;
    handleSort: (fieldId: string) => void;
    removeSortColumn: (fieldId: string) => void;
    clearAllSorting: () => void;
    handleCustomRangeClick: (fieldId: string, fieldLabel: string) => void;
    handleCustomRangeApply: (start: Date, end: Date) => void;
    handleCustomRangeClose: () => void;
    resetColumnWidthsCallback: () => void;
    handleColumnResizeCallback: (fieldId: string, width: number) => void;
    handleDragStartCallback: (e: React.DragEvent, fieldId: string) => void;
    handleDragOverCallback: (e: React.DragEvent) => void;
    handleDropCallback: (e: React.DragEvent, fieldId: string) => void;
    handleDragEndCallback: () => void;
    handleBulkEditField: (fieldId: string, value: FieldValue, itemIds: string[]) => Promise<void>;
    handleBulkEditAllFiltered: (fieldId: string, value: FieldValue) => Promise<void>;
    handleSelectAllPages: () => Promise<void>;
    handleFetchAllPages: (
      page: number,
      pageSize: number
    ) => Promise<{ data: TData[]; totalCount: number; hasNextPage: boolean }>;
    handleBulkAction: (actionId: string) => void;
    handleBodyScroll: () => void;
    handleHeaderScroll: () => void;
    addAdvancedFilter: (fieldId: string, filterType: string, value: FieldValue) => void;
    removeAdvancedFilter: (fieldId: string) => void;
    clearAllAdvancedFilters: () => void;
  };

  // Refs - DOM references
  refs: {
    containerRef: React.RefObject<HTMLDivElement | null>;
    tableHeaderScrollRef: React.RefObject<HTMLDivElement | null>;
    tableBodyRef: React.RefObject<HTMLDivElement | null>;
  };

  // Auth - User/auth info
  auth: {
    appUser: AuthenticatedUser;
    roleName: string | null;
  };

  // Hooks - Entity hooks for actions
  hooks: EntityHooks<TData>;

  // Utils - Style callbacks and utilities
  utils: {
    getColumnStyleCallback: (fieldId: string, fieldConfig: SimpleFieldConfig) => React.CSSProperties;
  };

  // Additional context
  groupingContext?: {
    isGrouped: boolean;
    expandedGroups: Set<string>;
    onToggleGroup: (groupKey: string) => void;
  };
  groupableFields?: Array<{ fieldId: string; label: string; type: string }>;
  openModal?: (
    id: string,
    data?: Record<string, unknown>,
    options?: { replace?: boolean }
  ) => void;

  // Action handlers - passed from parent
  onEdit?: (item: TData) => void;
  onView?: (item: TData) => void;
  onDelete?: (item: TData) => void;
  onInlineEdit?: (payload: Record<string, FieldValue>) => Promise<void>;
  onCreate?: () => void;
  onBulkAction?: (actionId: string, selectedItems: TData[]) => void;
  onOpenDetailModal?: (item: TData, ids: string[], index: number) => void;

  // UI elements and controls
  savedViewsControls?: React.ReactNode;
  configSavedViews?: Array<{ id: string; name: string; filters: SerializedViewState }>;

  // Infinite scroll and pagination
  loadMore?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  autoScrollEnabled?: boolean;
  propTotalItems?: number;
  propTotalPages?: number;
  propOnPageChange?: (page: number) => void;

  // Error state
  error?: Error | null;

  /**
   * True while the entity query is in flight. The body uses this to choose
   * between a loading skeleton and the empty state — without it, a slow first
   * load rendered "No <entity> found".
   */
  isLoading?: boolean;

  // Hide column headers (useful for card view)
  hideColumnHeaders?: boolean;

  // Hide table body (useful for header-only view in card mode)
  hideTableBody?: boolean;
}

const SimpleTableContext = createContext<SimpleTableContextValue<BaseEntity> | undefined>(
  undefined
);

interface SimpleTableContextProviderProps<TData extends BaseEntity> {
  value: SimpleTableContextValue<TData>;
  children: React.ReactNode;
}

/**
 * Provider component for SimpleTableContext
 */
export function SimpleTableContextProvider<TData extends BaseEntity>({
  value,
  children,
}: SimpleTableContextProviderProps<TData>) {
  // The context is stored at the BaseEntity boundary (it must serve every entity
  // type polymorphically); each consumer re-narrows via useSimpleTableContext<TData>().
  const baseValue = value as unknown as SimpleTableContextValue<BaseEntity>;
  return <SimpleTableContext.Provider value={baseValue}>{children}</SimpleTableContext.Provider>;
}

/**
 * Custom hook to access SimpleTableContext
 * Throws error if used outside of provider
 */
export function useSimpleTableContext<
  TData extends BaseEntity = BaseEntity,
>(): SimpleTableContextValue<TData> {
  const context = useContext(SimpleTableContext);
  if (!context) {
    throw new Error("useSimpleTableContext must be used within SimpleTableContextProvider");
  }
  // The context is stored at the BaseEntity boundary (provider erases TData to serve
  // every entity polymorphically); re-narrow here. This is the documented type-erased
  // context dispatch boundary (see ENTITY_REGISTRY) — `as unknown as` is required
  // because BaseEntity and TData don't structurally overlap enough for a direct cast.
  return context as unknown as SimpleTableContextValue<TData>;
}
